import * as ToastPrimitive from '@radix-ui/react-toast'
import { X, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useToastStore } from './toastStore'


const variantIcons = {
  default: null,
  success: CheckCircle,
  error: AlertCircle,
  message: MessageCircle,
}

const variantStyles = {
  default: 'border-neutral-700',
  success: 'border-l-4 border-l-green-500 border-neutral-700',
  error: 'border-l-4 border-l-red-500 border-neutral-700',
  message: 'border-l-4 border-l-primary-500 border-neutral-700',
}

const iconStyles = {
  default: 'text-neutral-400',
  success: 'text-green-500',
  error: 'text-red-500',
  message: 'text-primary-500',
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const { toasts, removeToast } = useToastStore()

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {children}
      {toasts.map((toast) => {
        const Icon = variantIcons[toast.variant || 'default']
        return (
          <ToastPrimitive.Root
            key={toast.id}
            duration={toast.duration}
            className={cn(
              'bg-neutral-800 border rounded-lg p-4 shadow-lg',
              'data-[state=open]:animate-slide-up',
              'data-[state=closed]:animate-fade-out',
              'data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)]',
              'data-[swipe=cancel]:translate-x-0',
              'data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]',
              variantStyles[toast.variant || 'default']
            )}
            onOpenChange={(open) => !open && removeToast(toast.id)}
          >
            <div className="flex items-start gap-3">
              {Icon && (
                <Icon
                  className={cn('h-5 w-5 mt-0.5', iconStyles[toast.variant || 'default'])}
                />
              )}
              <div className="flex-1 min-w-0">
                <ToastPrimitive.Title className="text-sm font-medium text-white">
                  {toast.title}
                </ToastPrimitive.Title>
                {toast.description && (
                  <ToastPrimitive.Description className="text-sm text-neutral-400 mt-1 line-clamp-2">
                    {toast.description}
                  </ToastPrimitive.Description>
                )}
              </div>
              {toast.action && (
                <ToastPrimitive.Action asChild altText={toast.action.label}>
                  <button
                    onClick={toast.action.onClick}
                    className="text-sm font-medium text-primary-400 hover:text-primary-300 transition-colors whitespace-nowrap"
                  >
                    {toast.action.label}
                  </button>
                </ToastPrimitive.Action>
              )}
              <ToastPrimitive.Close className="text-neutral-500 hover:text-white transition-colors">
                <X className="h-4 w-4" />
              </ToastPrimitive.Close>
            </div>
          </ToastPrimitive.Root>
        )
      })}
      <ToastPrimitive.Viewport className="fixed bottom-4 right-4 flex flex-col gap-2 w-96 max-w-[calc(100vw-2rem)] z-50 outline-none" />
    </ToastPrimitive.Provider>
  )
}

export { ToastPrimitive }
